// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import { ApiHandler, createHandlerChainRunner } from "@insite/client-framework/HandlerCreator";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import { DeviceType } from "@insite/client-framework/Types/ContentItemModel";
import { PageModel } from "@insite/client-framework/Types/PageProps";
import { CustomPagesApiParameter, getContentPages } from "@mcmc/App/Services/CustomContentService";
import McMcApplicationState from "@mcmc/App/Store/McMcApplicationState";
import { getPagesByNodeIds } from "@mcmc/App/Store/Selectors/ContentSelectors";

export interface ShellContext {
    defaultLanguageId: string;
    currentLanguageId: string;
    currentPersonaId: string;
    defaultPersonaId: string;
    currentDeviceType: DeviceType;
}

/**
 * The following function is an OOTB one that exists under PagesActionCreators Handler
 * It is not exported so we have to duplicate it here
 * @param state
 */
function getContextData(state: ApplicationState) {
    const { context } = state;
    if (context) {
        // determines if we are working in the public site
        const { website, session } = context;

        const defaultLanguages = website.languages!.languages!.filter(o => o.isDefault);
        const defaultPersonas = session.personas!.filter(o => o.isDefault);
        return {
            defaultLanguageId: defaultLanguages.length > 0 ? defaultLanguages[0].id : emptyGuid,
            currentLanguageId: session.language!.id,
            currentPersonaIds: session.personas ? session.personas.map(o => o.id) : [],
            currentDeviceType: session.deviceType as DeviceType,
            defaultPersonaId: defaultPersonas.length > 0 ? defaultPersonas[0].id : emptyGuid,
        };
    }

    // otherwise we are working in the shell so we need some hacky code
    const {
        defaultLanguageId,
        currentLanguageId,
        currentPersonaId,
        currentDeviceType,
        defaultPersonaId,
    } = (state as any).shellContext as ShellContext;
    return {
        defaultLanguageId,
        currentLanguageId,
        currentPersonaIds: [currentPersonaId],
        currentDeviceType,
        defaultPersonaId,
    };
}

interface Parameter extends CustomPagesApiParameter {
    onError?: (error: any) => void;
}

type HandlerType = ApiHandler<Parameter, PageModel[]>;

export const PopulateApiParameter: HandlerType = props => {
    props.apiParameter = props.parameter;
    delete props.apiParameter.onError;
};

export const RequestDataFromApi: HandlerType = async props => {
    props.apiResult = getPagesByNodeIds(props.getState() as McMcApplicationState, props.apiParameter.nodeIds);

    if (props.apiResult) {
        return false;
    }

    try {
        const result = await getContentPages(props.apiParameter);
        const sortedPages: PageModel[] = [];
        const nodeIds = props.parameter.nodeIds;
        const pages = result.pagesCollection;
        if (nodeIds?.length > 0 && pages?.length > 0) {
            nodeIds.every(id => {
                const matchPage = pages.find(page => page.nodeId === id);
                if (matchPage) {
                    const pageLink = getPageLinkByNodeId(props.getState(), matchPage.nodeId);
                    matchPage.fields = matchPage.fields || {};
                    matchPage.fields.customFullUrl = pageLink?.url;
                    sortedPages.push(matchPage);
                }
                return true;
            });
        }
        props.apiResult = sortedPages;
    } catch (error) {
        props.parameter.onError?.(error);
        return false;
    }
};

export const DispatchCompleteLoadPages: HandlerType = (props: any) => {
    const state = props.getState();
    props.dispatch({
        type: "xContent/CompleteLoadContentPages",
        contentPages: props.apiResult,
        parameter: props.parameter,
        ...getContextData(state),
    });
};

export const chain = [PopulateApiParameter, RequestDataFromApi, DispatchCompleteLoadPages];

const loadPages = createHandlerChainRunner(chain, "LoadPages");
export default loadPages;
